export interface BrandedSplitStripProps {
	brandedClicks: number | null;
	nonbrandedClicks: number | null;
}

/**
 * Branded vs non-branded click split (doc 15 slice 5d / doc 20 §2.2), fed by
 * the B2/G-O1 aggregate fields. Display-only strip — the backend is
 * aggregate-only, so a filter control would be a fake affordance (doc 50
 * PITFALL 5); nothing here invites a click.
 *
 * Grammar mirrors AudienceSplitBar: inline stats (ALL-CAPS tracked dash-label,
 * tabular dash-title counts tinted to their segment) above ONE two-segment
 * proportion bar — coral = branded, teal = non-branded (categorical pairing,
 * never judgment). Percentages live in the bar's aria-label + segment widths,
 * not as displayed numbers.
 *
 * Two honesty rules:
 *  - EITHER field null (or a zero-classified sample) = "not yet classified" —
 *    a muted pending line with feedforward, NEVER 0/0 counts and NEVER a
 *    50/50-looking ghost bar (B2 nullable-not-zero contract);
 *  - the microcopy names the sample: the split covers classified TOP QUERIES
 *    only, not the full query universe (branded + nonbranded ≠ total clicks).
 */
export function BrandedSplitStrip({ brandedClicks, nonbrandedClicks }: BrandedSplitStripProps) {
	const classified =
		brandedClicks !== null && nonbrandedClicks !== null && brandedClicks + nonbrandedClicks > 0;

	if (!classified) {
		return (
			<p className="px-5 py-3 text-dash-context text-muted-foreground">
				Query classification pending — branded split appears after the next sync
			</p>
		);
	}

	const branded = brandedClicks as number;
	const nonbranded = nonbrandedClicks as number;
	const brandedPct = Math.round((branded / (branded + nonbranded)) * 100);
	const nonbrandedPct = 100 - brandedPct;

	return (
		<div className="border-b px-5 py-3">
			<div className="flex flex-wrap items-baseline gap-x-2.5">
				<span className="text-dash-label uppercase tracking-wide text-muted-foreground">Branded clicks</span>
				<span className="text-dash-title font-semibold tabular text-coral-dark">{branded.toLocaleString()}</span>
				<span aria-hidden="true" className="text-dash-title text-muted-foreground">·</span>
				<span className="text-dash-label uppercase tracking-wide text-muted-foreground">Non-branded clicks</span>
				<span className="text-dash-title font-semibold tabular text-teal-dark">{nonbranded.toLocaleString()}</span>
			</div>
			<div
				className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-muted"
				role="img"
				aria-label={`Branded split: ${brandedPct}% branded, ${nonbrandedPct}% non-branded`}
			>
				<div className="bg-coral transition-all" style={{ width: `${brandedPct}%` }} />
				<div className="bg-teal transition-all" style={{ width: `${nonbrandedPct}%` }} />
			</div>
			<p className="mt-1.5 text-dash-context text-muted-foreground">
				classified top queries · Search Console lags ~3 days
			</p>
		</div>
	);
}
